import { useState, useMemo, useCallback, useEffect } from 'react';
import { usePersistedFilterState } from '@/hooks/usePersistedFilterState';
import { FilterRule, FilterField, FilterGroup, FilterEntity, FilterAttribute } from '@/types/filters';
import { Invoice } from './useInvoiceData';
import { supabase } from '@/integrations/supabase/client';

type Option = { value: string; label: string };

export const useFilterBuilder = (invoices: Invoice[]) => {
  const [draftGroups, setDraftGroups] = usePersistedFilterState<FilterGroup[]>('filters-invoices-draft', []);
  const [appliedGroups, setAppliedGroups] = usePersistedFilterState<FilterGroup[]>('filters-invoices-applied', []);

  const [optionsOverride, setOptionsOverride] = useState<{
    leadSources: Option[];
    leadOwners: Option[];
    cities: Option[];
    provinces: Option[];
    suburbs: Option[];
    tags: Option[];
  }>({ leadSources: [], leadOwners: [], cities: [], provinces: [], suburbs: [], tags: [] });

  // Fetch comprehensive filter options directly from customers (not limited to current invoices)
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const { data: customers, error } = await supabase
          .from('customers')
          .select('lead_source, suburb, city, province_state, tags, lead_owner_id');
        if (error) throw error;

        const leadSourcesSet = new Set<string>();
        const suburbsSet = new Set<string>();
        const citiesSet = new Set<string>();
        const provincesSet = new Set<string>();
        const tagsSet = new Set<string>();
        const ownerIdsSet = new Set<string>();

        (customers || []).forEach((c: any) => {
          if (c.lead_source) leadSourcesSet.add(c.lead_source);
          if (c.suburb) suburbsSet.add(c.suburb);
          if (c.city) citiesSet.add(c.city);
          if (c.province_state) provincesSet.add(c.province_state);
          if (Array.isArray(c.tags)) c.tags.filter(Boolean).forEach((t: string) => tagsSet.add(t));
          if (c.lead_owner_id) ownerIdsSet.add(c.lead_owner_id);
        });

        // Fetch profile names for lead owners
        let leadOwnerOptions: Option[] = [];
        const ownerIds = Array.from(ownerIdsSet);
        if (ownerIds.length > 0) {
          const { data: profiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', ownerIds);
          if (profilesError) throw profilesError;
          const nameMap = new Map<string, string>();
          (profiles || []).forEach((p: any) => {
            const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim();
            nameMap.set(p.id, fullName || p.id);
          });
          leadOwnerOptions = ownerIds.map(id => ({ value: id, label: nameMap.get(id) || id }));
        }

        setOptionsOverride({
          leadSources: Array.from(leadSourcesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          suburbs: Array.from(suburbsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          cities: Array.from(citiesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          provinces: Array.from(provincesSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          tags: Array.from(tagsSet).map(v => ({ value: v, label: v })).sort((a, b) => a.label.localeCompare(b.label)),
          leadOwners: leadOwnerOptions.sort((a, b) => a.label.localeCompare(b.label)),
        });
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };

    fetchOptions();
  }, []);

  // Get unique values for dynamic select options
  const dynamicOptions = useMemo(() => {
    const leadSources = [...new Set(invoices
      .map(inv => inv.customers?.lead_source)
      .filter(Boolean)
    )].map(source => ({ value: source, label: source })).sort((a, b) => a.label.localeCompare(b.label));

    const leadOwners = [...new Set(invoices
      .map(inv => inv.customers?.lead_owner_id)
      .filter(Boolean)
    )].map(ownerId => ({ value: ownerId, label: ownerId })).sort((a, b) => a.label.localeCompare(b.label));

    const cities = [...new Set(invoices
      .map(inv => inv.customers?.city)
      .filter(Boolean)
    )].map(city => ({ value: city, label: city })).sort((a, b) => a.label.localeCompare(b.label));

    const provinces = [...new Set(invoices
      .map(inv => inv.customers?.province_state)
      .filter(Boolean)
    )].map(province => ({ value: province, label: province })).sort((a, b) => a.label.localeCompare(b.label));

    const suburbs = [...new Set(invoices
      .map(inv => inv.customers?.suburb)
      .filter(Boolean)
    )].map(suburb => ({ value: suburb, label: suburb })).sort((a, b) => a.label.localeCompare(b.label));

    const tags = [...new Set(invoices
      .flatMap(inv => inv.customers?.tags || [])
      .filter(Boolean)
    )].map(tag => ({ value: tag, label: tag })).sort((a, b) => a.label.localeCompare(b.label));

    return {
      leadSources: optionsOverride.leadSources.length ? optionsOverride.leadSources : leadSources,
      leadOwners: optionsOverride.leadOwners.length ? optionsOverride.leadOwners : leadOwners,
      cities: optionsOverride.cities.length ? optionsOverride.cities : cities,
      provinces: optionsOverride.provinces.length ? optionsOverride.provinces : provinces,
      suburbs: optionsOverride.suburbs.length ? optionsOverride.suburbs : suburbs,
      tags: optionsOverride.tags.length ? optionsOverride.tags : tags,
    };
  }, [invoices, optionsOverride]);

  // Filter configuration
  const filterEntities: FilterEntity[] = [
    { key: 'invoice', label: 'Invoice' },
    { key: 'customer', label: 'Customer' }
  ];

  const filterAttributesByEntity: Record<string, FilterAttribute[]> = {
    invoice: [
      { key: 'status', label: "'s Status", type: 'select', options: [
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'cancelled', label: 'Cancelled' }
      ]},
      { key: 'invoice_type', label: "'s Type", type: 'select', options: [
        { value: 'full', label: 'Full' },
        { value: 'partial', label: 'Partial' },
        { value: 'percentage', label: 'Percentage' },
        { value: 'custom_amount', label: 'Custom Amount' }
      ]},
      { key: 'invoice_number', label: "'s Number", type: 'text' },
      { key: 'title', label: "'s Title", type: 'text' },
      { key: 'total_amount', label: "'s Total Amount", type: 'number' },
      { key: 'created_at', label: "'s Date Created", type: 'date' },
      { key: 'due_date', label: "'s Due Date", type: 'date' }
    ],
    customer: [
      { key: 'customer_type', label: "'s Type", type: 'select', options: [
        { value: 'residential', label: 'Residential' },
        { value: 'commercial', label: 'Commercial' }
      ]},
      { key: 'name', label: "'s Name", type: 'text' },
      { key: 'email', label: "'s Email", type: 'text' },
      { key: 'phone', label: "'s Phone", type: 'text' },
      { key: 'city', label: "'s City", type: 'select', options: dynamicOptions.cities },
      { key: 'province', label: "'s Province/State", type: 'select', options: dynamicOptions.provinces },
      { key: 'suburb', label: "'s Suburb", type: 'select', options: dynamicOptions.suburbs },
      { key: 'postal_code', label: "'s Postal Code", type: 'text' },
      { key: 'lead_source', label: "'s Lead Source", type: 'select', options: dynamicOptions.leadSources },
      { key: 'lead_owner', label: "'s Lead Owner", type: 'select', options: dynamicOptions.leadOwners },
      { key: 'tags', label: "'s Tags", type: 'select', options: dynamicOptions.tags }
    ]
  };

  // Generate filter fields for the FilterBuilder component
  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [];
    
    // Add invoice fields
    filterAttributesByEntity.invoice?.forEach(attr => {
      fields.push({
        key: `invoice.${attr.key}`,
        label: `Invoice${attr.label}`,
        type: attr.type,
        options: attr.options
      });
    });

    // Add customer fields
    filterAttributesByEntity.customer?.forEach(attr => {
      fields.push({
        key: `customer.${attr.key}`,
        label: `Customer${attr.label}`,
        type: attr.type,
        options: attr.options
      });
    });

    return fields;
  }, [dynamicOptions]);

  const addGroup = useCallback(() => {
    const newGroup: FilterGroup = {
      id: `group_${Date.now()}_${Math.random()}`,
      rules: [{
        id: `rule_${Date.now()}_${Math.random()}`,
        entity: '',
        attribute: '',
        operator: 'equals',
        value: null,
        logic: undefined,
      }],
      logic: draftGroups.length > 0 ? 'AND' : 'AND',
    };
    setDraftGroups(prev => [...prev, newGroup]);
  }, [draftGroups.length]);

  const updateGroup = useCallback((groupId: string, updates: Partial<FilterGroup>) => {
    setDraftGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, ...updates } : group
    ));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setDraftGroups(prev => prev.filter(group => group.id !== groupId));
  }, []);

  const addRuleToGroup = useCallback((groupId: string) => {
    const newRule: FilterRule = {
      id: `rule_${Date.now()}_${Math.random()}`,
      entity: '',
      attribute: '',
      operator: 'equals',
      value: null,
      logic: 'AND',
    };
    setDraftGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: [...group.rules, { ...newRule, logic: group.rules.length > 0 ? 'AND' : undefined }]
        };
      }
      return group;
    }));
  }, []);

  const updateRuleInGroup = useCallback((groupId: string, ruleId: string, updates: Partial<FilterRule>) => {
    setDraftGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          rules: group.rules.map(rule => 
            rule.id === ruleId ? { ...rule, ...updates } : rule
          )
        };
      }
      return group;
    }));
  }, []);

  const removeRuleFromGroup = useCallback((groupId: string, ruleId: string) => {
    setDraftGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        const updatedRules = group.rules.filter(rule => rule.id !== ruleId);
        // If no rules left, remove the group
        if (updatedRules.length === 0) {
          return null;
        }
        // Reset logic on first rule
        if (updatedRules.length > 0) {
          updatedRules[0].logic = undefined;
        }
        return {
          ...group,
          rules: updatedRules
        };
      }
      return group;
    }).filter(Boolean) as FilterGroup[]);
  }, []);

  const clearAllRules = useCallback(() => {
    setDraftGroups([]);
    setAppliedGroups([]);
  }, []);

  const isRuleComplete = useCallback((rule: FilterRule) => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;
    
    // Rules that don't need a value
    if (['is_empty', 'is_not_empty'].includes(rule.operator)) return true;
    
    // Rules that need a value
    if (rule.operator === 'between') {
      if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('due_date')) {
        return rule.value?.from && rule.value?.to;
      } else {
        return rule.value?.min !== undefined && rule.value?.max !== undefined;
      }
    }
    
    return rule.value !== null && rule.value !== undefined && rule.value !== '';
  }, []);

  const applyFilters = useCallback(() => {
    const completeGroups = draftGroups.map(group => ({
      ...group,
      rules: group.rules.filter(isRuleComplete)
    })).filter(group => group.rules.length > 0);
    setAppliedGroups(completeGroups);
  }, [draftGroups, isRuleComplete]);

  const hasUnappliedChanges = useMemo(() => {
    return JSON.stringify(draftGroups) !== JSON.stringify(appliedGroups);
  }, [draftGroups, appliedGroups]);

  const canApplyFilters = useMemo(() => {
    return draftGroups.length > 0 && draftGroups.some(group => 
      group.rules.length > 0 && group.rules.every(isRuleComplete)
    );
  }, [draftGroups, isRuleComplete]);

  const getFieldValue = useCallback((invoice: Invoice, rule: FilterRule) => {
    const customer = invoice.customers;
    
    if (rule.entity === 'invoice') {
      switch (rule.attribute) {
        case 'status':
          return invoice.status;
        case 'invoice_type':
          return invoice.invoice_type;
        case 'total_amount':
          return invoice.total_amount;
        case 'created_at':
          return invoice.created_at;
        case 'due_date':
          return invoice.due_date;
        case 'invoice_number':
          return invoice.invoice_number;
        case 'title':
          return invoice.title;
        default:
          return '';
      }
    }
    
    if (rule.entity === 'customer') {
      switch (rule.attribute) {
        case 'customer_type':
          return customer?.customer_type || '';
        case 'name':
          return customer ? `${customer.name} ${customer.last_name}` : '';
        case 'email':
          return customer?.emails || [];
        case 'phone':
          return customer?.cell_numbers || [];
        case 'city':
          return customer?.city || '';
        case 'province':
          return customer?.province_state || '';
        case 'suburb':
          return customer?.suburb || '';
        case 'postal_code':
          return customer?.postal_code || '';
        case 'lead_source':
          return customer?.lead_source || '';
        case 'lead_owner':
          return customer?.lead_owner_id || '';
        case 'tags':
          return customer?.tags || [];
        default:
          return '';
      }
    }
    
    return '';
  }, []);

  const evaluateRule = useCallback((invoice: Invoice, rule: FilterRule) => {
    if (!rule.entity || !rule.attribute || !rule.operator) return false;

    // Special handling for derived "overdue" status (status = sent AND due_date < now)
    if (rule.entity === 'invoice' && rule.attribute === 'status') {
      const isOverdue = invoice.status === 'sent'
        && !!invoice.due_date
        && new Date(invoice.due_date) < new Date();
      const values: any[] = Array.isArray(rule.value) ? rule.value : [rule.value];
      const includesOverdue = values.includes('overdue');

      if (includesOverdue) {
        const otherValues = values.filter(v => v !== 'overdue');
        const matchesOther = otherValues.includes(invoice.status);

        if (rule.operator === 'equals' || rule.operator === 'in') {
          return isOverdue || matchesOther;
        }
        if (rule.operator === 'not_equals' || rule.operator === 'not_in') {
          return !isOverdue && !otherValues.includes(invoice.status);
        }
      }
    }

    const fieldValue = getFieldValue(invoice, rule);



    switch (rule.operator) {
      case 'equals':
        if (Array.isArray(rule.value)) {
          if (Array.isArray(fieldValue)) {
            return fieldValue.some(v => rule.value.includes(v));
          }
          return rule.value.includes(fieldValue);
        }
        if (Array.isArray(fieldValue)) {
          return fieldValue.includes(rule.value);
        }
        return fieldValue === rule.value;
        
      case 'not_equals':
        if (Array.isArray(rule.value)) {
          if (Array.isArray(fieldValue)) {
            return !fieldValue.some(v => rule.value.includes(v));
          }
          return !rule.value.includes(fieldValue);
        }
        if (Array.isArray(fieldValue)) {
          return !fieldValue.includes(rule.value);
        }
        return fieldValue !== rule.value;
        
      case 'contains':
        if (Array.isArray(fieldValue)) {
          // Search within arrays (emails, phone numbers, tags)
          return fieldValue.some(item => 
            String(item).toLowerCase().includes(String(rule.value).toLowerCase())
          );
        }
        return String(fieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
        
      case 'not_contains':
        if (Array.isArray(fieldValue)) {
          return !fieldValue.some(item => 
            String(item).toLowerCase().includes(String(rule.value).toLowerCase())
          );
        }
        return !String(fieldValue).toLowerCase().includes(String(rule.value).toLowerCase());
        
      case 'greater_than':
        if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('due_date')) {
          if (Array.isArray(fieldValue)) return false; // Skip date comparison for array fields
          return new Date(fieldValue) > new Date(rule.value);
        } else {
          return Number(fieldValue) > Number(rule.value);
        }
        
      case 'less_than':
        if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('due_date')) {
          if (Array.isArray(fieldValue)) return false; // Skip date comparison for array fields
          return new Date(fieldValue) < new Date(rule.value);
        } else {
          return Number(fieldValue) < Number(rule.value);
        }
        
      case 'between':
        if (rule.attribute.includes('date') || rule.attribute.includes('created_at') || rule.attribute.includes('due_date')) {
          if (Array.isArray(fieldValue)) return false; // Skip date comparison for array fields
          const date = new Date(fieldValue);
          return date >= new Date(rule.value.from) && date <= new Date(rule.value.to);
        } else {
          const num = Number(fieldValue);
          return num >= rule.value.min && num <= rule.value.max;
        }
        
      case 'in':
        if (Array.isArray(fieldValue) && Array.isArray(rule.value)) {
          // Check if any values in fieldValue match any values in rule.value
          return fieldValue.some(item => rule.value.includes(item));
        }
        return Array.isArray(rule.value) && rule.value.includes(fieldValue);
        
      case 'not_in':
        if (Array.isArray(fieldValue) && Array.isArray(rule.value)) {
          return !fieldValue.some(item => rule.value.includes(item));
        }
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue);
        
      case 'is_empty':
        if (Array.isArray(fieldValue)) {
          return fieldValue.length === 0;
        }
        return !fieldValue || fieldValue === '' || fieldValue === null || fieldValue === undefined;
        
      case 'is_not_empty':
        if (Array.isArray(fieldValue)) {
          return fieldValue.length > 0;
        }
        return fieldValue && fieldValue !== '' && fieldValue !== null && fieldValue !== undefined;
        
      default:
        return false;
    }
  }, [getFieldValue]);

  // Apply filters to invoices with search and sorting
  const filteredInvoices = useMemo(() => {
    let filtered = invoices;

    // Apply filter groups
    if (appliedGroups.length > 0) {
      filtered = invoices.filter(invoice => {
        let groupResults: boolean[] = [];
        
        // Evaluate each group
        for (const group of appliedGroups) {
          if (group.rules.length === 0) continue;
          
          let groupResult = true;
          
          // Apply rules within the group
          for (let i = 0; i < group.rules.length; i++) {
            const rule = group.rules[i];
            const ruleResult = evaluateRule(invoice, rule);
            
            if (i === 0) {
              groupResult = ruleResult;
            } else {
              const logic = rule.logic || 'AND';
              if (logic === 'AND') {
                groupResult = groupResult && ruleResult;
              } else {
                groupResult = groupResult || ruleResult;
              }
            }
          }
          
          groupResults.push(groupResult);
        }
        
        // Combine group results
        if (groupResults.length === 0) return true;
        
        let finalResult = groupResults[0];
        for (let i = 1; i < groupResults.length; i++) {
          const groupLogic = appliedGroups[i].logic;
          if (groupLogic === 'AND') {
            finalResult = finalResult && groupResults[i];
          } else {
            finalResult = finalResult || groupResults[i];
          }
        }
        
        return finalResult;
      });
    }

    return filtered;
  }, [invoices, appliedGroups, evaluateRule]);

  const hasActiveFilters = appliedGroups.length > 0;

  return {
    draftGroups,
    appliedGroups,
    setDraftGroups,
    setAppliedGroups,
    filteredInvoices,

    hasActiveFilters,
    hasUnappliedChanges,
    canApplyFilters,
    // Actions
    addGroup,
    updateGroup,
    removeGroup,
    addRuleToGroup,
    updateRuleInGroup,
    removeRuleFromGroup,
    clearAllRules,
    applyFilters,
    // Filter configuration
    filterFields,
    filterEntities,
    filterAttributesByEntity
  };
};